import { useEffect, useState } from 'react'

/**
 * What a phone actually shows of the page (tester reports, 19 Oct 2026, iPhone).
 *
 * On a phone the screen can show less than the page's own viewport: a pinch zoom, the zoom iOS
 * makes when a small box takes focus, and the on-screen keyboard all shrink the visual viewport
 * while `position: fixed` stays laid out against the whole layout viewport. A full-screen sheet
 * opened then lands partly or wholly off the part the person sees ("the signature adoption screen
 * doesn't appear"), and a box the keyboard rises over stays under it. These helpers place a sheet
 * on the visible part and move a focused box back into it.
 */

/** A box in the layout viewport's CSS pixels, the coordinates `position: fixed` uses. */
export interface ViewportBox {
  readonly top: number
  readonly left: number
  readonly width: number
  readonly height: number
}

/** The fields of `window.visualViewport` this reads; a test passes a plain object. */
export interface VisualViewportLike {
  readonly offsetTop: number
  readonly offsetLeft: number
  readonly width: number
  readonly height: number
}

/**
 * The visible part of the layout viewport, or null when it is all of it (no zoom, no keyboard) or
 * the browser cannot say: the sheet's own `inset: 0` is then already right. Half a pixel of
 * difference is rounding, not a zoom.
 */
export function visibleBox(
  viewport: VisualViewportLike | null | undefined,
  layout: { readonly width: number; readonly height: number },
): ViewportBox | null {
  if (viewport === null || viewport === undefined) return null
  const { offsetTop, offsetLeft, width, height } = viewport
  if (![offsetTop, offsetLeft, width, height].every(Number.isFinite) || width <= 0 || height <= 0)
    return null
  const whole =
    Math.abs(offsetTop) < 0.5 &&
    Math.abs(offsetLeft) < 0.5 &&
    Math.abs(width - layout.width) < 0.5 &&
    Math.abs(height - layout.height) < 0.5
  return whole ? null : { top: offsetTop, left: offsetLeft, width, height }
}

/**
 * How far to scroll the page down (positive) or up (negative) so a box, in client coordinates,
 * sits inside the visible band with a margin on each side; 0 when it already does. A box taller
 * than the band keeps its top edge in view, where the caret starts.
 */
export function revealDelta(
  box: { readonly top: number; readonly bottom: number },
  visible: { readonly top: number; readonly bottom: number },
  margin: { readonly top: number; readonly bottom: number } = { top: 24, bottom: 24 },
): number {
  const top = visible.top + margin.top
  const bottom = visible.bottom - margin.bottom
  if (bottom <= top) return 0
  if (box.top < top) return box.top - top
  if (box.bottom > bottom) return Math.min(box.bottom - bottom, box.top - top)
  return 0
}

function currentVisibleBox(): ViewportBox | null {
  if (typeof window === 'undefined') return null
  return visibleBox(window.visualViewport, {
    width: document.documentElement.clientWidth,
    height: window.innerHeight,
  })
}

/**
 * The visible part of the screen while `active`, following zoom, pan and the keyboard; null when
 * the whole layout viewport shows.
 */
export function useVisibleBox(active = true): ViewportBox | null {
  const [box, setBox] = useState<ViewportBox | null>(() => (active ? currentVisibleBox() : null))
  useEffect(() => {
    const viewport = typeof window === 'undefined' ? null : window.visualViewport
    if (!active || viewport === null || viewport === undefined) {
      setBox(null)
      return
    }
    const update = (): void =>
      setBox((current) => {
        const next = currentVisibleBox()
        const same =
          current === next ||
          (current !== null &&
            next !== null &&
            current.top === next.top &&
            current.left === next.left &&
            current.width === next.width &&
            current.height === next.height)
        return same ? current : next
      })
    update()
    viewport.addEventListener('resize', update)
    viewport.addEventListener('scroll', update)
    window.addEventListener('resize', update)
    window.addEventListener('keyboardDidShow', update)
    window.addEventListener('keyboardDidHide', update)
    return () => {
      viewport.removeEventListener('resize', update)
      viewport.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
      window.removeEventListener('keyboardDidShow', update)
      window.removeEventListener('keyboardDidHide', update)
    }
  }, [active])
  return box
}

/**
 * Keeps the focused box in view when the visible part of the screen shrinks under it — the
 * on-screen keyboard rising over a box low on the page. Only a focus inside an element matching
 * `within` (the document's fields) is moved; the page itself is never scrolled for anything else.
 */
export function useKeepFocusedInView(within: string): void {
  useEffect(() => {
    const viewport = typeof window === 'undefined' ? null : window.visualViewport
    if (viewport === null || viewport === undefined) return
    let frame = 0
    const reveal = (): void => {
      const active = document.activeElement
      if (!(active instanceof HTMLElement) || active.closest(within) === null) return
      const rect = active.getBoundingClientRect()
      let delta = revealDelta(rect, {
        top: viewport.offsetTop,
        bottom: viewport.offsetTop + viewport.height,
      })
      // The box may sit in a scrolling pane (the editor's page): that pane moves first, then the
      // page takes whatever is left.
      for (
        let pane = active.parentElement;
        pane !== null && delta !== 0;
        pane = pane.parentElement
      ) {
        const overflow = getComputedStyle(pane).overflowY
        if (overflow !== 'auto' && overflow !== 'scroll') continue
        const before = pane.scrollTop
        pane.scrollTop = before + delta
        delta -= pane.scrollTop - before
      }
      if (delta !== 0) window.scrollBy({ top: delta, behavior: 'auto' })
    }
    // The keyboard animates in: the last size of the run is the one to settle on.
    const onResize = (): void => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(reveal)
    }
    viewport.addEventListener('resize', onResize)
    document.addEventListener('focusin', onResize)
    window.addEventListener('keyboardDidShow', onResize)
    return () => {
      cancelAnimationFrame(frame)
      viewport.removeEventListener('resize', onResize)
      document.removeEventListener('focusin', onResize)
      window.removeEventListener('keyboardDidShow', onResize)
    }
  }, [within])
}

/** The custom properties sheets and bottom bars read (`@signbento/ui` and the app's styles). */
export const VIEWPORT_PROPERTIES = {
  top: '--op-visible-top',
  height: '--op-visible-height',
  bottom: '--op-keyboard-inset',
} as const

/**
 * The values for those properties while the keyboard is up: the visible band's top and height,
 * and how much of the layout viewport's bottom it hides. Null when nothing is hidden (no keyboard,
 * or a shell that already shrinks the page above it, like the installed app).
 */
export function keyboardBand(
  viewport: VisualViewportLike | null | undefined,
  layout: { readonly width: number; readonly height: number },
): { readonly top: number; readonly height: number; readonly bottom: number } | null {
  const box = visibleBox(viewport, layout)
  if (box === null) return null
  const bottom = Math.max(0, layout.height - (box.top + box.height))
  if (bottom < 0.5 && box.top < 0.5) return null
  return { top: box.top, height: box.height, bottom }
}

/**
 * Keeps `--op-visible-top`, `--op-visible-height` and `--op-keyboard-inset` on the root element
 * while a box has the keyboard up, so a bottom sheet or bar sits on the keyboard instead of behind
 * it (a browser keeps `position: fixed` on the whole page). A pinch zoom with no box focused leaves
 * them unset: the bars stay where the person put the page. Returns its removal.
 */
export function installViewportInsets(
  doc: Document,
  hasKeyboard: (active: Element | null) => boolean,
): () => void {
  const view = doc.defaultView
  const viewport = view?.visualViewport
  if (view === null || viewport === null || viewport === undefined) return () => undefined
  const root = doc.documentElement
  let frame = 0
  const apply = (): void => {
    const band = hasKeyboard(doc.activeElement)
      ? keyboardBand(viewport, { width: root.clientWidth, height: view.innerHeight })
      : null
    if (band === null) {
      for (const name of Object.values(VIEWPORT_PROPERTIES)) root.style.removeProperty(name)
      return
    }
    root.style.setProperty(VIEWPORT_PROPERTIES.top, `${Math.round(band.top)}px`)
    root.style.setProperty(VIEWPORT_PROPERTIES.height, `${Math.round(band.height)}px`)
    root.style.setProperty(VIEWPORT_PROPERTIES.bottom, `${Math.round(band.bottom)}px`)
  }
  const schedule = (): void => {
    view.cancelAnimationFrame(frame)
    frame = view.requestAnimationFrame(apply)
  }
  viewport.addEventListener('resize', schedule)
  viewport.addEventListener('scroll', schedule)
  doc.addEventListener('focusin', schedule)
  doc.addEventListener('focusout', schedule)
  view.addEventListener('resize', schedule)
  view.addEventListener('keyboardDidShow', schedule)
  view.addEventListener('keyboardDidHide', schedule)
  apply()
  return () => {
    view.cancelAnimationFrame(frame)
    viewport.removeEventListener('resize', schedule)
    viewport.removeEventListener('scroll', schedule)
    doc.removeEventListener('focusin', schedule)
    doc.removeEventListener('focusout', schedule)
    view.removeEventListener('resize', schedule)
    view.removeEventListener('keyboardDidShow', schedule)
    view.removeEventListener('keyboardDidHide', schedule)
    for (const name of Object.values(VIEWPORT_PROPERTIES)) root.style.removeProperty(name)
  }
}

/**
 * Brings a whole form field — label, box and the error under it — into view after its box takes
 * focus, and again once the keyboard has finished rising over it. Focusing alone shows only the
 * box, which left "Fill this in to continue" under the keyboard.
 */
export function focusField(control: HTMLElement | null, within = '.op-field'): void {
  if (control === null) return
  control.focus({ preventScroll: true })
  const field = control.closest<HTMLElement>(within) ?? control
  const reveal = (): void => field.scrollIntoView({ block: 'nearest', behavior: 'auto' })
  reveal()
  const viewport = typeof window === 'undefined' ? null : window.visualViewport
  if (viewport === null || viewport === undefined) return
  let settled = 0
  const onResize = (): void => {
    window.clearTimeout(settled)
    settled = window.setTimeout(reveal, 80)
  }
  viewport.addEventListener('resize', onResize)
  window.setTimeout(() => viewport.removeEventListener('resize', onResize), 1200)
}
